<script setup>
import { useMetrics } from "@/composables/use.metrics";
import { defineEmits, defineProps, reactive, ref } from "vue";
import StarRatings from "vue3-star-ratings";
import { useStore } from "vuex";

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});

const emits = defineEmits(["next", "close"]);
const store = useStore()

const showLiked = ref(true);
const showDisliked = ref(false);
const pending = ref(false);
const { eventTypes, hitType, hit } = useMetrics();

const state = reactive({
  positive: '',
  negative: '',
  rating: 0,
});

function updateRating(rating) {
  state.rating = Math.ceil(rating);
}

function closeModal(){
    emits('close')
}

async function sendData() {
  try {
    pending.value = true;
    const formData = new FormData();
    Object.entries(state).forEach(([key, value]) => {
      formData.append(key, value);
    });
    hit(eventTypes.FIREWORK, hitType.REVIEW, state);
    await store.dispatch('saveFireworkFeedback', formData);
    emits('next')
  } finally {
    pending.value = false;
  }
}
</script>

<template>
  <Transition name="fade">
    <div v-if="props.show" class="shell">
      <div class="modal">
        <div class="modal-content">
          <div class="fill">
            <div class="caption">
              <p class="title">Пройдите опрос</p>
              <button class="close" @click="closeModal">
                <svg class="close__img" width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_7509_946)">
                    <rect width="21" height="21" rx="10.5" fill="white" />
                    <path d="M10.5 0C4.65 0 0 4.65 0 10.5C0 16.35 4.65 21 10.5 21C16.35 21 21 16.35 21 10.5C21 4.65 16.35 0 10.5 0ZM14.55 15.75L10.5 11.7L6.45 15.75L5.25 14.55L9.3 10.5L5.25 6.45L6.45 5.25L10.5 9.3L14.55 5.25L15.75 6.45L11.7 10.5L15.75 14.55L14.55 15.75Z" fill="#5D6C85" />
                  </g>
                  <defs>
                    <clipPath id="clip0_7509_946">
                      <rect width="21" height="21" rx="10.5" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
            <div class="block">
              <button class="block-head" @click="showLiked = !showLiked">
                <p class="title">Что понравилось?</p>
                <div
                  class="block__button"
                  :class="{'block__button--active': showLiked}">
                  <svg
                    width="25"
                    height="14"
                    viewBox="0 0 25 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 1.5L12.5 12.5L24 1.5"
                      stroke="#393939"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
              </button>
              <div class="block-content" v-if="showLiked">
                <textarea v-model="state.positive" class="block-content__textarea"></textarea>
              </div>
            </div>
            <div class="block">
              <button class="block-head" @click="showDisliked = !showDisliked">
                <p class="title">Что не понравилось?</p>
                <div
                  class="block__button"
                  :class="{'block__button--active': showDisliked}">
                  <svg
                    width="25"
                    height="14"
                    viewBox="0 0 25 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 1.5L12.5 12.5L24 1.5"
                      stroke="#393939"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
              </button>
              <div class="block-content" v-if="showDisliked">
                <textarea v-model="state.negative" class="block-content__textarea"></textarea>
              </div>
            </div>
            <div class="grade">
              <p class="grade__title">Оцените полученный опыт от 1 до 5</p>
              <StarRatings
                class="grade-stars"
                :starsCounts="5"
                :modelValue="state.rating"
                inactiveColor="#9dc4f8ab"
                starColor="#ffc200"
                @update:modelValue="updateRating"
              />
            </div>
          </div>

          <div class="actions">
            <button class="link-default" :disabled="pending" @click="sendData">Отправить</button>
            <button class="link-default link-default--gray" :disabled="pending" @click="closeModal">Закрыть</button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.shell {
  background: #00288c;
  z-index: 1000000;
}

.modal {
  padding: 0;
  max-width: 874px;
  border-radius: 40px;
  pointer-events: all;
  backdrop-filter: blur(8px);
  background: rgba(157, 196, 248, 0.3);
  padding: 24px;
  width: 100%;

  &-content {
    backdrop-filter: blur(80px);
    background: rgba(229, 229, 229, 0.3);
    border-radius: 24px;
    padding: 24px;
  }
  @media (max-width:1000px) {
    padding: 16px;
    &-content {
      padding: 16px;
    }
  }
}

.fill {
  padding: 16px 24px;
  width: 100%;
  background: #fff;
  border-radius: 17px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.title {
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.01em;
  color: #000;
}

.caption {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.close {
  display: flex;
  background: none;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease-in-out;

  &:hover {
    opacity: 0.5;
  }

  img {
    width: 100%;
  }
}
.actions {
  margin-top: 24px;
  display: flex;
  gap: 16px;
}
.link-default {
  &--gray {
    background: #ecf2fa;
    color: #8b98aa;
  }
}
.block {
  padding: 16px 30px;
  border-radius: 16px;
  background: #ecf2fa;
  &-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  &__button {
    transition: all 0.5s ease-in-out;
    &--active {
      transform: rotate(180deg);
    }
  }
  &-content {
    margin-top: 8px;
    &__textarea {
      border: none;
      min-height: 141px;
    }
  }
}

.grade {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  &__title {
    font-size: 20px;
    letter-spacing: 0.03em;
    color: #000;
  }
}
@media (max-width: 1300px) {
  .title {
    font-size: 20px;
    text-align: left;
  }
  .text {
    font-size: 16px;
  }
  .fill {
    padding: 0;
  }
  .modal-content {
    background: #fff;
  }
  .block {
    padding: 16px 12px;
  }

  .grade {
    flex-direction: column;
    &__title {
      font-size: 16px;
    }
  }
}

@media (max-width:1000px) {
  .title {
    font-size: 18px;
    text-align: left;
  }
  .actions{
    flex-direction: column;
  }
  .link-default{
    padding:0.625rem 1rem;
    font-size: 0.88rem;
  }
}
</style>
